import React, { useEffect, useState } from 'react';
import { Loader2, XCircle } from 'lucide-react';
import { getUserProfile } from '../api';
import { consumeOAuthCallback } from '../utils/publicRoutes';

const OAuthCallback = ({ onLoginSuccess, showNotification, setActiveView, setIsAuthenticated, refreshCredits, setProductDetails }) => {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed) {
      console.log('🔄 OAuth callback already processed, skipping...');
      return;
    }

    const handleOAuthCallback = async () => {
      const { token, success, error } = consumeOAuthCallback();

      if (success === 'true' && token) {
        localStorage.setItem('token', token);

        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.role) {
            localStorage.setItem('role', payload.role);
          }
        } catch (e) {
          console.log('Could not decode token for role');
        }

        let needsPersonaSelection = false;
        try {
          const profileResponse = await getUserProfile(token);
          const personas = profileResponse.data?.data?.user?.userPersonas || [];
          needsPersonaSelection = personas.length === 0;
        } catch (profileError) {
          console.log('Could not load profile for persona check:', profileError);
        }

        setStatus('success');
        setMessage(needsPersonaSelection
          ? 'Almost there! Select your business persona to finish setup.'
          : 'Authentication successful! Welcome to Fashdeck!');

        if (onLoginSuccess) {
          await onLoginSuccess();
        } else if (setIsAuthenticated) {
          setIsAuthenticated(true);
          setActiveView('generate');
        }

        showNotification(
          needsPersonaSelection
            ? 'Please select your business persona in My Account settings.'
            : 'Authentication successful! Welcome to Fashdeck!',
          needsPersonaSelection ? 'info' : 'success'
        );

        if (refreshCredits) {
          refreshCredits();
        }
      } else {
        setStatus('error');
        setMessage(error ? decodeURIComponent(error) : 'Authentication failed. Please try again.');
        showNotification(error ? decodeURIComponent(error) : 'Authentication failed. Please try again.', 'error');

        setTimeout(() => {
          setActiveView('login');
        }, 1500);
      }
      
      // Mark as processed to prevent re-execution
      setHasProcessed(true);
    };

    handleOAuthCallback();
  }, []); // Empty dependency array - only run once on mount

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c186c] via-[#5424e4] to-[#17bcf7] flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-8 text-center border border-white/20">
        <div className="mb-8">
          {status === 'loading' && (
            <>
              <div className="relative mb-6">
                <div className="w-24 h-24 mx-auto overflow-hidden rounded-2xl bg-white shadow-lg shadow-black/20 ring-1 ring-white/40 flex items-center justify-center">
                  <img src="/logo.png?v=20260711" alt="Fashdeck" className="h-full w-full object-contain opacity-90" />
                  <Loader2 size={28} className="absolute text-white/90 animate-spin" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Welcome to Fashdeck</h2>
              <p className="text-white/80 text-lg">Setting up your account...</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="relative mb-6">
                <div className="w-24 h-24 mx-auto overflow-hidden rounded-2xl bg-white shadow-lg shadow-black/20 ring-1 ring-white/40 flex items-center justify-center">
                  <img src="/logo.png?v=20260711" alt="Fashdeck" className="h-full w-full object-contain" />
                </div>
              </div>
              <h2 className="text-4xl font-bold text-white mb-3">Welcome to Fashdeck</h2>
              <p className="text-white/90 text-xl mb-2">Authentication successful</p>
              <p className="text-white/70 text-lg">Your creative journey starts now</p>
              <div className="mt-4 flex justify-center">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="relative mb-6">
                <div className="w-24 h-24 mx-auto bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                  <XCircle size={48} className="text-white" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Oops!</h2>
              <p className="text-white/80 text-lg mb-2">Authentication failed</p>
              <p className="text-white/60 text-sm">{message}</p>
            </>
          )}
        </div>
        
        {status === 'success' && (
          <div className="mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <h3 className="text-white font-semibold text-lg mb-3">🚀 Ready to create amazing content?</h3>
              <p className="text-white/70 text-sm mb-4">Get started with your first marketing kit</p>
              <button
                onClick={() => setActiveView('dashboard')}
                className="w-full bg-[#5424e4] text-white font-bold py-3 px-6 rounded-xl hover:bg-[#3d18b8] transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Enter Dashboard
              </button>
            </div>
          </div>
        )}
        
        {status === 'error' && (
          <div className="mt-6">
            <button
              onClick={() => setActiveView('landing')}
              className="bg-white/20 backdrop-blur-sm text-white font-semibold py-3 px-6 rounded-xl hover:bg-white/30 transition-all duration-300 border border-white/30"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
